import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, FileSpreadsheet, X, ArrowLeft, Loader2, Download } from "lucide-react";
import { useCreateGroupByExcel } from "@/hooks/activity/useCreateGroupByExcel";

const CreateGroupByExcel = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { createGroup, loading } = useCreateGroupByExcel();

  const handleFileSelect = (file: File) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!groupName.trim()) {
      return;
    }

    const success = await createGroup(groupName, selectedFile);
    if (success) {
      navigate('/admin/patient-groups');
    }
  };

  const handleBack = () => {
    navigate('/admin/patient-groups');
  };

  const isSubmitDisabled = !groupName.trim() || loading;

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/templates/dummy-patient-ids.xlsx';
    link.download = 'dummy-patient-ids.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Patient Group List
          </Button>
          <span className="text-muted-foreground">&gt;&gt;</span>
          <span className="text-muted-foreground">Create New Group</span>
        </div>
        
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create Patient Group by Excel</h1>
            <p className="text-muted-foreground mt-2">Upload an Excel file with patient IDs to create a new patient group</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            className="text-primary hover:text-primary"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Group Information</CardTitle>
          <CardDescription>
            Provide a name for your patient group and upload an Excel file containing patient IDs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Group Name Input */}
          <div className="space-y-2">
            <Label htmlFor="groupName" className="text-sm font-semibold">
              Group Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="groupName"
              placeholder="Enter patient group name..."
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              disabled={loading}
              className="max-w-md"
            />
          </div>

          {/* File Upload Area */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">
              Excel File
            </Label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}
                ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary hover:bg-muted/50'}
              `}
              onClick={() => !loading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileInput}
                className="hidden"
                disabled={loading}
              />
              
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-green-500" />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-foreground">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    disabled={loading}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Drag and drop your Excel file here, or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Supported formats: .xls, .xlsx
                    </p>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              The Excel file should contain a column with patient IDs. Download the template file above for reference.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-8"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Group...
                </>
              ) : (
                'Create Group'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateGroupByExcel;
